import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Inscription, List, useResponsive } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { EMPTY_FIELD_VALUE, SHORT_DATE_FORMAT } from '@/common/constants';
import routes from '@/common/routes';
import FilePreview from '@/modules/MyDocuments/components/FilePreview/FilePreview';
import { Receipt } from '@/modules/MyDocuments/myDocuments.types';

interface Props extends Receipt {
  onView: (id?: string) => void;
  onDelete: (id?: string) => void;
}

const ReceiptsListItem: React.FC<Props> = ({
  id,
  claimId,
  documentId,
  fileName,
  fileUrl,
  providerName,
  createdAt,
  onView,
  onDelete,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const date = useMemo(() => {
    if (!createdAt) {
      return EMPTY_FIELD_VALUE;
    }

    return dayjs(createdAt).format(SHORT_DATE_FORMAT);
  }, [createdAt]);

  const getClaimLink = useCallback((claimId?: string) => {
    return routes.EXPENSES_TRANSACTION_DETAILS_CLAIM(claimId);
  }, []);

  const claim = useMemo(() => {
    const formattedLink = getClaimLink(claimId);

    return (
      <a
        href={claimId ? formattedLink : undefined}
        data-testid={`receipt-list_item_${id}_claim-link`}
        target="_blank"
        style={{ textDecoration: 'none', display: isMobile ? 'inline-block' : undefined }}
        rel="noreferrer"
      >
        <Box direction="row" align="center" style={{ cursor: 'pointer' }}>
          <Inscription
            color={providerName ? 'textAccent' : 'textBody'}
            textAlign="center"
            weight={400}
            size="medium"
          >
            {providerName || EMPTY_FIELD_VALUE}
          </Inscription>
        </Box>
      </a>
    );
  }, [getClaimLink, claimId, id, isMobile, providerName]);

  const handleView = useCallback(() => {
    onView(id);
  }, [id, onView]);

  const handleDelete = useCallback(() => {
    onDelete(documentId);
  }, [documentId, onDelete]);
  if (isMobile) {
    return (
      <List.Item
        direction="column"
        pad={isMobile ? 'spacing12' : { vertical: 'spacing12', horizontal: 'spacing24' }}
        testId={`receipt-list_item_${id}`}
      >
        <Box direction="row" align="center" width="100%">
          <FilePreview
            fileUrl={fileUrl}
            width="45px"
            height="45px"
            margin={{ right: 'spacing12' }}
            testId={`receipt-${id}_file-preview`}
          />

          <Box fill margin={{ right: 'spacing12' }}>
            <List.Item.Text
              testId={`receipt-list_item_${id}_file-name`}
              truncate
            >
              {fileName}
            </List.Item.Text>
          </Box>

          <List.Item.Options>
            <List.Item.Options.Item testId={`receipt-list_item_${id}_view-action`} onClick={handleView}>
              {t('View')}
            </List.Item.Options.Item>

            <List.Item.Options.Item
              testId={`receipt-list_item_${id}_delete-action`}
              color="danger"
              onClick={handleDelete}
            >
              {t('Delete')}
            </List.Item.Options.Item>
          </List.Item.Options>
        </Box>
        <Box height="1px" background="border2" width="100%" margin={{ vertical: 'spacing12' }} />
        <Box direction="row" width="100%">
          <Box fill>
            <List.Item.Text weight={400} flex={1} testId={`receipt-list_item_${id}_claim`}>
              {`${t('Claim')}: `} {claim}
            </List.Item.Text>
          </Box>
          <div>
            <List.Item.Text weight={400} flex={1.5} testId={`receipt-list_item_${id}_date`}>
              {date}
            </List.Item.Text>
          </div>
        </Box>
      </List.Item>
    );
  }
  return (
    <List.Item pad={{ vertical: 'spacing12', horizontal: 'spacing24' }} testId={`receipt-list_item_${id}`}>
      <Box style={{ flex: 0.6 }} justify="center">
        <FilePreview fileUrl={fileUrl} testId={`receipt-${id}_file-preview`} />
      </Box>

      <List.Item.Text flex={1.5} testId={`receipt-list_item_${id}_file-name`}>
        {fileName}
      </List.Item.Text>

      <List.Item.Text weight={400} flex={1.5} testId={`receipt-list_item_${id}_date`}>
        {date}
      </List.Item.Text>

      <List.Item.Text weight={400} flex={1} testId={`receipt-list_item_${id}_claim`}>
        {claim}
      </List.Item.Text>

      <Box style={{ flex: 0.4 }} align="end">
        <List.Item.Options>
          <List.Item.Options.Item testId={`receipt-list_item_${id}_view-action`} onClick={handleView}>
            {t('View')}
          </List.Item.Options.Item>

          <List.Item.Options.Item
            testId={`receipt-list_item_${id}_delete-action`}
            color="danger"
            onClick={handleDelete}
          >
            {t('Delete')}
          </List.Item.Options.Item>
        </List.Item.Options>
      </Box>
    </List.Item>
  );
};

export default ReceiptsListItem;
